import {lazy, useRef, useEffect, memo} from 'react';
import {useSelector} from 'react-redux';
import {cn} from '@/utils';
import {useImmer} from "use-immer";
import {coreModule} from "@reduxjs/toolkit/query";

const PromotionBox = ({nodeId, baseProduct}) => {
    const intervalId = useRef(null);
    const titleRef = useRef();
    const [componentState, updateComponentState] = useImmer({
        fetchSuccessful: null,
        isActive: false,
        // Future variable is not used as the endpoint is not providing it.
        // I left it in there as we may want to use it in the future.
        isActiveFuture: false,

        background_color: null,
        base_products: [],
        long_title: '',
        description: '',
        timer: {
            start: null,
            end: null,
        }
    });

    useEffect(() => {
        const fetchPromoBox = async () => {
            const response = await fetch(`/api/active-promotion-box?node_id=${nodeId}`);
            const data = await response.json();

            // If there is no promotion box, the response is an empty array.
            if (data.length == 0) return;

            const {
                background_color,
                long_title,
                description,
                timer,
                base_products
            } = data;

            const currentTime = new Date().getTime();
            const hasStarted = timer.start*1000 < currentTime;
            const hasNotEnded = timer.end !== null ? timer.end*1000 >= currentTime : true;
            const isActive = hasStarted && hasNotEnded;
            const isActiveFuture = timer.start*1000 >= currentTime;

            updateComponentState((draft) => {
                draft.fetchSuccessful = true;
                draft.background_color = background_color;
                draft.long_title = long_title;
                draft.description = description;
                draft.timer = timer;
                draft.base_products = base_products;
                draft.isActive = isActive;
                draft.isActiveFuture = isActiveFuture;
            });
        };

        fetchPromoBox().catch((error) => {
            updateComponentState((draft) => {
                draft.fetchSuccessful = false;
            });
        });
    }, []);

    useEffect(() => {
        if (intervalId.current === null && (componentState.isActive === true || componentState.isActiveFuture === true)) {
            updateVariables();
            intervalId.current = setInterval(updateVariables, 1000);

            function updateVariables() {
              const currentTime = new Date().getTime();
              const hasMatch = componentState.base_products.some(sub =>
                baseProduct.toLowerCase().includes(sub.toLowerCase())
              );
              const hasStarted = componentState.timer.start * 1000 < currentTime;
              const hasNotEnded = componentState.timer.end !== null ? componentState.timer.end * 1000 >= currentTime : true;
              const isActive = hasStarted && hasNotEnded && hasMatch === true;
              const isActiveFuture = componentState.timer.start * 1000 >= currentTime;

              const difference = componentState.timer.end !== null ? componentState.timer.end * 1000 - currentTime : 0;
              let days = 0, hours = 0, minutes = 0, seconds = 0;

              if (difference > 0) {
                  days = Math.floor(difference / (1000 * 60 * 60 * 24)).toString().padStart(2, '0');
                  hours = Math.floor((difference / (1000 * 60 * 60)) % 24).toString().padStart(2, '0');
                  minutes = Math.floor((difference / (1000 * 60)) % 60).toString().padStart(2, '0');
                  seconds = Math.floor((difference / 1000) % 60).toString().padStart(2, '0');
              }

              updateComponentState((draft) => {
                  draft.isActive = isActive;
                  draft.isActiveFuture = isActiveFuture;
              });

              if (titleRef.current && titleRef.current.querySelector('span.promotion-box-timer') && componentState.timer.end !== null) {
                titleRef.current.querySelector('span.promotion-box-timer').innerHTML = `${days}:${hours}:${minutes}:${seconds}`;
              }
            }
        } else if (componentState.isActive === false && componentState.isActiveFuture === false) {
            clearInterval(intervalId.current);
            intervalId.current = null;
        }
    }, [componentState.isActive, componentState.isActiveFuture]);

    useEffect(() => {
      const hasMatch = componentState.base_products.some(sub =>
        baseProduct.toLowerCase().includes(sub.toLowerCase())
      );
      const currentTime = new Date().getTime();

      const hasStarted = componentState.timer.start * 1000 < currentTime;
      const hasNotEnded = componentState.timer.end !== null ? componentState.timer.end * 1000 >= currentTime : true;
      const isActive = hasStarted && hasNotEnded && hasMatch === true;
      const isActiveFuture = componentState.timer.start * 1000 >= currentTime;

      updateComponentState((draft) => {
        draft.isActive = isActive;
        draft.isActiveFuture = isActiveFuture;
      });

    }, [baseProduct]);

    return (
        <If condition={componentState.isActive === true}>
            <div style={{backgroundColor: '#' + (componentState.background_color ?? '')}} className="flex flex-col mb-2xl px-xl py-md">
              <div ref={titleRef} dangerouslySetInnerHTML={{ __html: componentState.long_title }}></div>
              <div dangerouslySetInnerHTML={{ __html: componentState.description }}></div>
            </div>
        </If>
    );
};

export default PromotionBox;
